/**
 * Ustawienia — the booking policy (T-B1): two groups of the mobile settings panel's rows, and beside
 * them the phone showing what a client would see.
 *
 * Every row is a title, one sentence of what the setting DOES, and one control: a switch, or a value
 * pill that opens its chips (`PolicyPickPopover`). „Kiedy pracuję" leads to the Grafik tab. Every
 * change is sent at once (`usePolicySettings`), so the switches are named as switches; a refused
 * change is taken back and said quietly under its row. The credits switch is NOT here — it lives
 * in Rozliczenia's header (wave 3) — and the per-client exception lives on the client's card.
 *
 * One gate over the policy and the hours: the two groups hold their shape as bars until both have
 * answered, so nothing moves when they do.
 */

import { useTranslation } from 'react-i18next';

import skeleton from '~/shared/ui/skeleton.module.css';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import { Toggle } from '~/shared/ui/Toggle';
import { ChevronGlyph } from '~/shared/ui/VideoGlyphs';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { usePolicySettings } from '../lib/usePolicySettings';
import type { SettingsGroup, SettingsRow } from '../model/policySettings';
import { ClientPhone } from './ClientPhone';
import { PolicyPickPopover } from './PolicyPickPopover';
import frame from './bookingTab.module.css';
import styles from './SettingsTab.module.css';

interface SettingsTabProps {
  readonly onOpenSchedule: () => void;
}

export function SettingsTab({ onOpenSchedule }: SettingsTabProps) {
  const { t } = useTranslation();
  const settings = usePolicySettings();
  const { gate, content } = settings;
  const waiting = content.kind === 'waiting';
  const bar = gate.skeleton ? String(skeleton.bar) : '';

  const rowOf = (row: SettingsRow) => {
    const id = 'id' in row ? row.id : null;
    const refusal = id === null ? undefined : settings.refusals[id];
    const refusalId = id === null ? undefined : `booking-settings-refusal-${id}`;
    const describedBy = refusal === undefined ? undefined : refusalId;
    return (
      <div className={styles.row}>
        <div className={styles.rowText}>
          <span className={row.kind === 'pick' && !row.enabled ? styles.titleQuiet : styles.title}>
            {row.title}
          </span>
          {row.effect === null ? null : (
            <span
              className={
                waiting
                  ? `${String(styles.effect)} ${String(styles.hiddenInk)} ${bar}`
                  : styles.effect
              }
            >
              {row.effect}
            </span>
          )}
          {refusal === undefined ? null : (
            <span id={refusalId} className={styles.refusal} role="status">
              {refusal}
            </span>
          )}
        </div>
        {waiting ? (
          row.kind === 'toggle' ? (
            <span className={`${String(styles.toggleBar)} ${bar}`} />
          ) : row.kind === 'pick' ? (
            <span className={`${String(styles.value)} ${String(styles.hiddenInk)} ${bar}`}>
              {row.value}
              {/* The pill's chevron takes width too; without it the row's sentence was 20px wider
               * in the wait than in the answer. */}
              <ChevronGlyph
                className={`${String(styles.valueGlyph)} ${String(styles.hiddenInk)}`}
                direction="down"
              />
            </span>
          ) : (
            <>
              {row.hours === null ? null : (
                <span className={`${String(styles.hours)} ${String(styles.hiddenInk)} ${bar}`}>
                  {row.hours}
                </span>
              )}
              <span className={`${String(styles.openBar)} ${bar}`} />
            </>
          )
        ) : row.kind === 'toggle' ? (
          <Toggle
            checked={row.value}
            label={row.title}
            writesAtOnce
            describedBy={describedBy}
            onChange={(next) => {
              settings.change(row.id, next);
            }}
          />
        ) : row.kind === 'pick' ? (
          <PolicyPickPopover
            row={row}
            pick={settings.pick(row.id)}
            describedBy={describedBy}
            onChoose={(value) => {
              settings.change(row.id, value);
            }}
          />
        ) : (
          <>
            {row.hours === null ? null : <span className={styles.hours}>{row.hours}</span>}
            <button
              type="button"
              className={styles.open}
              aria-label={t('web:booking.settings.openSchedule')}
              onClick={onOpenSchedule}
            >
              <ChevronGlyph className={styles.openGlyph} direction="right" />
            </button>
          </>
        )}
      </div>
    );
  };

  // The wait and the answer are the same rows in the same boxes: the default policy's shape
  // (`DEFAULT_POLICY`) with its values and sentences under bars, then the trainer's own.
  const groupOf = (group: SettingsGroup) => (
    <section
      key={group.id}
      className={styles.group}
      aria-labelledby={waiting ? undefined : `booking-settings-${group.id}`}
      aria-hidden={waiting ? true : undefined}
    >
      <h2 id={`booking-settings-${group.id}`} className={styles.eyebrow}>
        {group.title}
      </h2>
      <div className={`${String(styles.card)} ${waiting ? '' : gate.reveal}`}>
        {group.rows.map((row, index) => (
          <div key={'id' in row ? row.id : 'schedule'}>
            {index === 0 ? null : <hr className={styles.divider} />}
            {rowOf(row)}
          </div>
        ))}
      </div>
    </section>
  );

  const [booking, cancellation] =
    content.kind === 'failed' ? [undefined, undefined] : content.groups;

  return (
    <div className={frame.scroller}>
      <p className={frame.intro}>{t('web:booking.settings.intro')}</p>
      <div className={frame.layout} aria-busy={gate.waiting}>
        {gate.waiting ? (
          <span className={hidden.srOnly}>{t('web:booking.settings.loading')}</span>
        ) : null}
        {content.kind === 'failed' ? (
          <div
            className={`${String(frame.card)} ${String(frame.failed)} ${gate.reveal}`}
            role="alert"
          >
            <p className={frame.failedTitle}>{t('web:booking.settings.unreadTitle')}</p>
            <button type="button" className={styles.retry} onClick={settings.retry}>
              {t('web:booking.settings.retry')}
            </button>
          </div>
        ) : (
          <div className={styles.columns}>
            <div className={styles.column}>{booking === undefined ? null : groupOf(booking)}</div>
            <div className={styles.column}>
              {cancellation === undefined ? null : groupOf(cancellation)}
              <p className={styles.note}>{t('web:booking.trainer.settings.note')}</p>
            </div>
          </div>
        )}
        <ClientPhone state={settings.preview} zone={settings.zone} />
      </div>
      <LiveRegionAnnouncer message={settings.announcement} />
    </div>
  );
}
